// Draft persistence for the Field Mode pre-flight + Completion forms.
//
// The operator may take hours to complete a single sign-off; if they leave
// the page mid-edit (a phone call, a ride to another orchard, a tab switch
// the OS reclaimed) the entered PPE / times / weather / quantities must NOT
// be lost. We persist a per-session draft to localStorage, keyed by session
// id, and restore it on mount. Drafts are cleared after a successful submit
// (online or offline-queued).
//
// Phase 4 of spray_session_workflow_20260427 unified the pre-flight and
// completion screens onto a single per-session draft key. The pre-flight
// writes the PPE checklist + an editable Start time; the completion screen
// reads the carry-over, adds its own end time + weather + quantities, and
// writes back. DRAFT_VERSION bumps to 2 to silently discard any v1 draft
// saved by the pre-Phase-4 client (the v1 schema's PPE/startTime fields
// meant slightly different things — startTime was a HH:MM string).
//
// Rationale for localStorage over Dexie: drafts are tiny in-flight UI state,
// not network-durable submission records. Keeping the offline submission
// queue (Dexie spraySignOffs) and the in-flight draft (localStorage) in
// separate stores keeps each store's purpose obvious. localStorage is also
// synchronous, which simplifies the React state hookup considerably.
//
// Track: spray_session_workflow_20260427 / Phase 4, Task 7
// (extends operator_completion_20260412).

const STORAGE_KEY_PREFIX = 'spray-completion-draft-';
// v2: weather inputs switched from free-text/number to categorical radios
// (Cold/Optimal/Hot, Calm/Breeze/Strong, None/Light/Heavy) plus an optional
// override comment. v1 drafts cannot be safely restored into the new shape,
// so the version bump silently discards them.
const DRAFT_VERSION = 2;

/**
 * Wire-format of a saved draft. The shape is intentionally a superset of
 * the form's writable state so future fields can land additively.
 *
 * `version` lets us silently discard drafts that were saved by a different
 * code revision — preventing crashes when fields change.
 */
export interface SprayCompletionDraft {
  version: number;
  ppe?: Array<{ item: string; checked: boolean }>;
  startTime?: string;
  endTime?: string;
  /**
   * Operator-confirmed END-of-session wind category, written by the
   * close sign-off form (FieldModeCompletionForm). Kept un-prefixed for
   * backward compatibility with v2 drafts saved before the open sign-off
   * panel was introduced. Open sign-off uses startWindCategory.
   */
  windCategory?: string;
  /** Operator-confirmed END-of-session temperature category. */
  temperatureCategory?: string;
  /** Operator-confirmed END-of-session precipitation category. */
  precipitationCategory?: string;
  /**
   * Operator-confirmed START-of-session wind category, written by the
   * open sign-off panel (OpenSignOffWeatherPanel). Stored separately from
   * windCategory so the open and close sign-off forms can persist their
   * own snapshots without clobbering each other.
   */
  startWindCategory?: string;
  /** Operator-confirmed START-of-session temperature category. */
  startTemperatureCategory?: string;
  /** Operator-confirmed START-of-session precipitation category. */
  startPrecipitationCategory?: string;
  /**
   * Raw start-of-session weather reading the open sign-off panel used
   * to auto-categorise. The close sign-off picks this up so the
   * /complete payload's weatherSnapshots.start.systemReported carries
   * actual numerical readings, not just category strings.
   *
   * Track: spray_weather_enhancements_20260429 / Phase 5 wiring fix.
   */
  startWeatherReading?: {
    timestamp?: string;
    temp?: number;
    windSpeed?: number;
    precipitation?: number;
    condition?: string;
    humidity?: number;
    pressure?: number;
  };
  /** Optional comment when overriding the system-reported category. */
  overrideComment?: string;
  quantities?: Record<string, string>;
  savedAt: string;
}

const keyFor = (sessionId: string) => `${STORAGE_KEY_PREFIX}${sessionId}`;

function safeStorage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Read the saved draft for a session. Returns null when none exists, the
 * payload is corrupt, or the version doesn't match — in all error cases we
 * fail silently so a bad draft can never block the form from rendering.
 */
export function readDraft(sessionId: string): SprayCompletionDraft | null {
  const storage = safeStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(keyFor(sessionId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.version !== DRAFT_VERSION) return null;
    return parsed as SprayCompletionDraft;
  } catch {
    return null;
  }
}

/**
 * Write a draft. Errors (quota exceeded, serialisation failure) are
 * swallowed — losing draft persistence is preferable to crashing the form.
 */
export function writeDraft(
  sessionId: string,
  patch: Omit<SprayCompletionDraft, 'version' | 'savedAt'>
): void {
  const storage = safeStorage();
  if (!storage) return;
  try {
    const payload: SprayCompletionDraft = {
      version: DRAFT_VERSION,
      savedAt: new Date().toISOString(),
      ...patch,
    };
    storage.setItem(keyFor(sessionId), JSON.stringify(payload));
  } catch {
    // ignore
  }
}

/**
 * Remove the draft for a session. Called after a successful submit (online
 * or offline-queued) so the operator does not see stale data the next time
 * they open the form for the same session.
 */
export function clearDraft(sessionId: string): void {
  const storage = safeStorage();
  if (!storage) return;
  try {
    storage.removeItem(keyFor(sessionId));
  } catch {
    // ignore
  }
}

/**
 * Test helper: returns the localStorage key used for a session id so
 * tests can read it directly without re-implementing the prefix logic.
 */
export function __keyForTesting(sessionId: string): string {
  return keyFor(sessionId);
}
